import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SectionHero, StatGrid, MetricCard, AdminCard } from "@/components/admin/primitives";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { RefreshCw, TrendingUp, AlertCircle, Info } from "lucide-react";
import {
  getInterpretationQualityMetrics,
  getInterpretationQualityRuns,
  type TimeWindow,
  type InterpretationQuality,
  type RunRecord,
} from "@/lib/api/admin/analysis-quality";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

export default function AnalysisQualityDashboard() {
  const [timeWindow, setTimeWindow] = useState<TimeWindow>("30d");
  const [viewMode, setViewMode] = useState<"count" | "percentage">("count");
  const [qualityFilter, setQualityFilter] = useState<InterpretationQuality | "all">("all");
  const [repairedMajorOnly, setRepairedMajorOnly] = useState(false);
  const [selectedRun, setSelectedRun] = useState<RunRecord | null>(null);

  const { data: metrics, isLoading: metricsLoading, error: metricsError, refetch: refetchMetrics } = useQuery({
    queryKey: ["interpretation-quality-metrics", timeWindow],
    queryFn: () => getInterpretationQualityMetrics(timeWindow),
    refetchInterval: 60000, // Refresh every minute
  });

  const { data: runs, isLoading: runsLoading, error: runsError, refetch: refetchRuns } = useQuery({
    queryKey: ["interpretation-quality-runs", qualityFilter, repairedMajorOnly],
    queryFn: () =>
      getInterpretationQualityRuns({
        interpretation_quality: qualityFilter === "all" ? undefined : qualityFilter,
        repairedMajorOnly: repairedMajorOnly || undefined,
      }),
    refetchInterval: 60000,
  });

  const chartData = metrics?.distribution.map((d) => ({
    date: new Date(d.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    Clean: viewMode === "count" ? d.clean : metrics.totalEvaluated > 0 ? (d.clean / (d.clean + d.repairedMinor + d.repairedMajor)) * 100 : 0,
    "Repaired Minor": viewMode === "count" ? d.repairedMinor : metrics.totalEvaluated > 0 ? (d.repairedMinor / (d.clean + d.repairedMinor + d.repairedMajor)) * 100 : 0,
    "Repaired Major": viewMode === "count" ? d.repairedMajor : metrics.totalEvaluated > 0 ? (d.repairedMajor / (d.clean + d.repairedMinor + d.repairedMajor)) * 100 : 0,
  })) || [];

  const getQualityBadge = (quality: InterpretationQuality) => {
    if (quality === "clean") {
      return <Badge className="bg-green-500/10 text-green-700 border-green-500/20">Clean</Badge>;
    } else if (quality === "repaired_minor") {
      return <Badge className="bg-yellow-500/10 text-yellow-700 border-yellow-500/20">Repaired Minor</Badge>;
    } else if (quality === "repaired_major") {
      return <Badge className="bg-red-500/10 text-red-700 border-red-500/20">Repaired Major</Badge>;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-surface pb-20">
      <div className="max-w-admin mx-auto px-admin-content-padding py-8">
        {/* Hero Section */}
        <SectionHero
          title="Interpretation Quality Dashboard"
          description="Monitor interpretation quality metrics and review individual analysis runs. Interpretation Quality is a diagnostic signal, not a correctness score."
          gradient="info"
          actions={
            <>
              <Select value={timeWindow} onValueChange={(v) => setTimeWindow(v as TimeWindow)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">7 days</SelectItem>
                  <SelectItem value="30d">30 days</SelectItem>
                  <SelectItem value="90d">90 days</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={() => { refetchMetrics(); refetchRuns(); }} disabled={metricsLoading || runsLoading} variant="outline" size="sm">
                <RefreshCw className={`w-4 h-4 mr-2 ${metricsLoading || runsLoading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </>
          }
        />

        {/* Overview Panel */}
        <div className="mt-8">
          <h2 className="text-admin-section font-semibold text-foreground mb-4">Overview</h2>
          {metricsLoading ? (
            <StatGrid columns={5}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-32" />
              ))}
            </StatGrid>
          ) : metricsError ? (
            <div className="bg-admin-warning/10 border border-admin-warning/20 rounded-lg p-4 text-center">
              <p className="text-sm text-muted-foreground mb-2">
                Error loading metrics: {metricsError instanceof Error ? metricsError.message : "Unknown error"}
              </p>
            </div>
          ) : (
            <TooltipProvider>
              <StatGrid columns={5}>
                <div className="relative">
                  <MetricCard
                    title="Total Runs Evaluated"
                    value={metrics?.totalEvaluated.toLocaleString() ?? "0"}
                    description="Runs with Phase-1 and Phase-2 validation complete"
                    size="large"
                  />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="absolute top-2 right-2 text-muted-foreground hover:text-foreground">
                        <Info className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Runs where Phase-1 detection completed and Phase-2 validation passed after repair.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="relative">
                  <MetricCard
                    title="Clean"
                    value={`${metrics?.clean.percentage.toFixed(1) ?? 0}%`}
                    description={`${metrics?.clean.count.toLocaleString() ?? 0} runs`}
                    size="large"
                    accentColor="success"
                  />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="absolute top-2 right-2 text-muted-foreground hover:text-foreground">
                        <Info className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>No repairs were needed. The LLM output was structurally valid and required no correction.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="relative">
                  <MetricCard
                    title="Repaired Minor"
                    value={`${metrics?.repairedMinor.percentage.toFixed(1) ?? 0}%`}
                    description={`${metrics?.repairedMinor.count.toLocaleString() ?? 0} runs`}
                    size="large"
                    accentColor="warning"
                  />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="absolute top-2 right-2 text-muted-foreground hover:text-foreground">
                        <Info className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Minor repairs were applied (1-2 repairs, all phase_presence rule). This indicates minor anchoring correction was needed.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="relative">
                  <MetricCard
                    title="Repaired Major"
                    value={`${metrics?.repairedMajor.percentage.toFixed(1) ?? 0}%`}
                    description={`${metrics?.repairedMajor.count.toLocaleString() ?? 0} runs`}
                    size="large"
                    accentColor="danger"
                  />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="absolute top-2 right-2 text-muted-foreground hover:text-foreground">
                        <Info className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Major repairs were applied (3+ repairs, or merge rule, or duplicate flaw repairs). Repaired Major indicates semantic correction, not failure.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="relative">
                  <MetricCard
                    title="Excluded Runs"
                    value={metrics?.excluded.toLocaleString() ?? "0"}
                    description="Runs not evaluated (interpretation_quality is null)"
                    size="large"
                  />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="absolute top-2 right-2 text-muted-foreground hover:text-foreground">
                        <Info className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Runs where interpretation_quality is null despite Phase-1 completion and Phase-2 validation passing. These runs are not evaluated.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </StatGrid>
            </TooltipProvider>
          )}
        </div>

        {/* Distribution Visualization */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-admin-section font-semibold text-foreground">Distribution Over Time</h2>
            <Select value={viewMode} onValueChange={(v) => setViewMode(v as "count" | "percentage")}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="count">Count</SelectItem>
                <SelectItem value="percentage">Percentage</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {metricsLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : metricsError ? (
            <div className="bg-admin-warning/10 border border-admin-warning/20 rounded-lg p-4 text-center">
              <p className="text-sm text-muted-foreground">Error loading distribution data</p>
            </div>
          ) : chartData.length === 0 ? (
            <div className="bg-muted/50 border border-muted rounded-lg p-8 text-center">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No data available for the selected time window</p>
            </div>
          ) : (
            <AdminCard title="" accentColor="primary">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="Clean" stackId="a" fill="#10b981" />
                  <Bar dataKey="Repaired Minor" stackId="a" fill="#eab308" />
                  <Bar dataKey="Repaired Major" stackId="a" fill="#ef4444" />
                </BarChart>
              </ResponsiveContainer>
            </AdminCard>
          )}
        </div>

        {/* Run-Level Table */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-admin-section font-semibold text-foreground">Run Details</h2>
            <div className="flex items-center gap-2">
              <Select
                value={qualityFilter}
                onValueChange={(v) => {
                  setQualityFilter(v as InterpretationQuality | "all");
                  if (v !== "all") setRepairedMajorOnly(false);
                }}
              >
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Quality Levels</SelectItem>
                  <SelectItem value="clean">Clean Only</SelectItem>
                  <SelectItem value="repaired_minor">Repaired Minor Only</SelectItem>
                  <SelectItem value="repaired_major">Repaired Major Only</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant={repairedMajorOnly ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setRepairedMajorOnly(!repairedMajorOnly);
                  if (!repairedMajorOnly) setQualityFilter("all");
                }}
              >
                Repaired Major Only
              </Button>
            </div>
          </div>
          {runsLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : runsError ? (
            <div className="bg-admin-warning/10 border border-admin-warning/20 rounded-lg p-4 text-center">
              <p className="text-sm text-muted-foreground">
                Error loading runs: {runsError instanceof Error ? runsError.message : "Unknown error"}
              </p>
            </div>
          ) : !runs || runs.length === 0 ? (
            <div className="bg-muted/50 border border-muted rounded-lg p-8 text-center">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No runs found matching the selected filters</p>
            </div>
          ) : (
            <AdminCard title="" accentColor="primary">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Run ID</th>
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Date</th>
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Quality</th>
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Repair Count</th>
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Repair Types</th>
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Final Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((run) => (
                      <tr key={run.id} className="border-b hover:bg-muted/50">
                        <td className="p-3 text-sm font-mono text-xs">
                          <button
                            onClick={() => setSelectedRun(run)}
                            className="text-primary hover:underline cursor-pointer"
                          >
                            {run.id.substring(0, 8)}...
                          </button>
                        </td>
                        <td className="p-3 text-sm">
                          {new Date(run.created_at).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </td>
                        <td className="p-3">{getQualityBadge(run.interpretation_quality)}</td>
                        <td className="p-3 text-sm">{run.repair_count}</td>
                        <td className="p-3 text-sm">
                          {run.repair_types.length > 0 ? run.repair_types.join(", ") : "—"}
                        </td>
                        <td className="p-3">
                          {run.final_status === "succeeded" ? (
                            <Badge className="bg-green-500/10 text-green-700 border-green-500/20">Succeeded</Badge>
                          ) : run.final_status === "failed" ? (
                            <Badge className="bg-red-500/10 text-red-700 border-red-500/20">Failed</Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </AdminCard>
          )}
        </div>

        {/* Run Detail Drawer */}
        <Sheet open={selectedRun !== null} onOpenChange={(open) => !open && setSelectedRun(null)}>
          <SheetContent className="sm:max-w-2xl overflow-y-auto">
            {selectedRun && (
              <>
                <SheetHeader>
                  <SheetTitle>Run Details</SheetTitle>
                  <SheetDescription>
                    Run ID: {selectedRun.id}
                  </SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-6">
                  {/* Basic Info */}
                  <div>
                    <h3 className="text-sm font-semibold mb-2">Basic Information</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Date:</span>
                        <span>{new Date(selectedRun.created_at).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Interpretation Quality:</span>
                        {getQualityBadge(selectedRun.interpretation_quality)}
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Repair Count:</span>
                        <span>{selectedRun.repair_count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Final Status:</span>
                        {selectedRun.final_status === "succeeded" ? (
                          <Badge className="bg-green-500/10 text-green-700 border-green-500/20">Succeeded</Badge>
                        ) : selectedRun.final_status === "failed" ? (
                          <Badge className="bg-red-500/10 text-red-700 border-red-500/20">Failed</Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Interpretation Quality Explanation */}
                  <div>
                    <h3 className="text-sm font-semibold mb-2">Interpretation Quality</h3>
                    <div className="bg-muted/50 rounded-lg p-4 text-sm space-y-2">
                      {selectedRun.interpretation_quality === "clean" && (
                        <>
                          <p className="font-medium">Clean</p>
                          <p className="text-muted-foreground">
                            No repairs were needed. The LLM output was structurally valid and required no correction.
                          </p>
                        </>
                      )}
                      {selectedRun.interpretation_quality === "repaired_minor" && (
                        <>
                          <p className="font-medium">Repaired Minor</p>
                          <p className="text-muted-foreground">
                            Minor repairs were applied (1-2 repairs, all phase_presence rule). This indicates minor anchoring correction was needed.
                          </p>
                        </>
                      )}
                      {selectedRun.interpretation_quality === "repaired_major" && (
                        <>
                          <p className="font-medium">Repaired Major</p>
                          <p className="text-muted-foreground">
                            Major repairs were applied (3+ repairs, or merge rule, or duplicate flaw repairs). This indicates semantic correction was needed, not failure.
                          </p>
                        </>
                      )}
                      <p className="text-xs text-muted-foreground mt-3 italic">
                        Interpretation Quality is a diagnostic signal, not a correctness score. Repaired Major indicates semantic correction, not failure.
                      </p>
                    </div>
                  </div>

                  {/* Phase 2 Repairs */}
                  {selectedRun.phase_2_repairs.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold mb-2">Phase 2 Repairs</h3>
                      <div className="bg-muted/50 rounded-lg p-4">
                        <pre className="text-xs overflow-x-auto">
                          {JSON.stringify(selectedRun.phase_2_repairs, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* Why This Matters */}
                  <div>
                    <h3 className="text-sm font-semibold mb-2">Why This Matters</h3>
                    <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-sm text-muted-foreground">
                      <p className="mb-2">
                        This dashboard helps QA monitor interpretation quality over time and detect regressions caused by prompt or pipeline changes.
                      </p>
                      <p>
                        High rates of "Repaired Major" may indicate prompt clarity issues, while "Clean" rates show when the LLM produces structurally valid outputs without correction.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}

